/**
 * Gateway routes of an app, read from the annotation that the backend
 * `byx-catalog` module writes on the API `<app>-gateway` (IC-2, AD-7).
 *
 * The annotation is entity data, so it is parsed defensively (SR-03):
 * `JSON.parse` in try/catch, then a runtime guard that accepts arrays and
 * strings only, copies the allowed fields into new objects and drops
 * everything else. Invalid input gives `[]`. Nothing here throws or logs.
 */

/** Annotation with the JSON array of `GatewayExposure` (one per env). */
export const GATEWAY_ROUTES_ANNOTATION = 'byxcapital.com/gateway-routes';

/** Annotation with the gateway domain(s) of the app, comma-separated. */
export const GATEWAY_DOMAIN_ANNOTATION = 'byxcapital.com/gateway-domain';

/** Separator of the domains in `byxcapital.com/gateway-domain`. */
export const GATEWAY_DOMAIN_SEPARATOR = ',';

/** The exposure values a route may have (`GatewayRoute.exposure`). */
export const GATEWAY_EXPOSURES = ['internal', 'public'] as const;

export type GatewayExposureValue = (typeof GATEWAY_EXPOSURES)[number];

/** `GatewayOperation.authentication` of a route without authentication. */
export const ANONYMOUS_AUTHENTICATION = 'anonymous';

/**
 * Every field the guard reads, by backend interface. Fields that are not
 * listed (`service`, `backendPath`, `justification`, the values path…) are
 * never read. The contract test checks these names against the backend.
 */
export const GATEWAY_FIELDS = {
  GatewayExposure: ['env', 'domain', 'routes'],
  GatewayRoute: ['path', 'exposure', 'operations'],
  GatewayOperation: ['method', 'scopes', 'authentication'],
} as const;

export type GatewayOperationView = {
  method: string;
  scopes: string[];
  authentication?: string;
};

export type GatewayRouteView = {
  path: string;
  exposure: GatewayExposureValue;
  operations: GatewayOperationView[];
};

export type GatewayEnv = {
  env: string;
  domain?: string;
  routes: GatewayRouteView[];
};

type Fields<K extends keyof typeof GATEWAY_FIELDS> =
  (typeof GATEWAY_FIELDS)[K][number];

/** A plain JSON object (not an array, not null). */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** An own property only: never one inherited through `__proto__`. */
function own<K extends string>(
  record: Record<string, unknown>,
  key: K,
): unknown {
  return Object.hasOwn(record, key) ? record[key] : undefined;
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

function readOperation(value: unknown): GatewayOperationView | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const field = (key: Fields<'GatewayOperation'>) => own(value, key);
  const method = nonEmptyString(field('method'));
  const scopes = field('scopes');
  if (!method || (scopes !== undefined && !Array.isArray(scopes))) {
    return undefined;
  }
  const authentication = nonEmptyString(field('authentication'));
  return {
    method: method.toLocaleUpperCase('en-US'),
    scopes: (scopes ?? []).filter(
      (scope): scope is string => nonEmptyString(scope) !== undefined,
    ),
    ...(authentication ? { authentication } : {}),
  };
}

function isExposure(value: unknown): value is GatewayExposureValue {
  return (GATEWAY_EXPOSURES as readonly unknown[]).includes(value);
}

function readRoute(value: unknown): GatewayRouteView | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const field = (key: Fields<'GatewayRoute'>) => own(value, key);
  const path = nonEmptyString(field('path'));
  const exposure = field('exposure');
  const operations = field('operations');
  if (!path || !isExposure(exposure) || !Array.isArray(operations)) {
    return undefined;
  }
  return {
    path,
    exposure,
    operations: operations
      .map(readOperation)
      .filter((op): op is GatewayOperationView => op !== undefined),
  };
}

function readEnv(value: unknown): GatewayEnv | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const field = (key: Fields<'GatewayExposure'>) => own(value, key);
  const env = nonEmptyString(field('env'));
  const routes = field('routes');
  if (!env || !Array.isArray(routes)) {
    return undefined;
  }
  const domain = nonEmptyString(field('domain'));
  return {
    env,
    ...(domain ? { domain } : {}),
    routes: routes
      .map(readRoute)
      .filter((route): route is GatewayRouteView => route !== undefined),
  };
}

/**
 * The gateway declaration of an app, one entry per environment, from the
 * raw annotation value. Invalid JSON or a value of the wrong shape gives
 * `[]`; entries of the wrong shape inside a valid array are dropped.
 */
export function parseGatewayRoutes(raw: unknown): GatewayEnv[] {
  if (typeof raw !== 'string') {
    return [];
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) {
    return [];
  }
  return parsed
    .map(readEnv)
    .filter((env): env is GatewayEnv => env !== undefined);
}

/** The gateway domains in the domain annotation, without blanks or repeats. */
export function parseGatewayDomains(raw: unknown): string[] {
  if (typeof raw !== 'string') {
    return [];
  }
  const domains = raw
    .split(GATEWAY_DOMAIN_SEPARATOR)
    .map(part => part.trim())
    .filter(Boolean);
  return [...new Set(domains)];
}

/** One method of one route, as a row of the compact route list. */
export type GatewayRouteRow = {
  key: string;
  method: string;
  path: string;
  exposure: GatewayExposureValue;
  anonymous: boolean;
};

/** Environments read first when the same route is declared in several. */
const ENV_PRIORITY = ['prod', 'staging', 'dev'];

function envRank(env: string): number {
  const index = ENV_PRIORITY.indexOf(env);
  return index === -1 ? ENV_PRIORITY.length : index;
}

/**
 * One row per method and path across environments. A route declared in
 * several environments is shown once, as declared in the highest one
 * (prod, then staging, then dev), which is the exposure that matters.
 */
export function gatewayRouteRows(envs: GatewayEnv[]): GatewayRouteRow[] {
  const rows = new Map<string, GatewayRouteRow>();
  const ordered = [...envs].sort((a, b) => envRank(a.env) - envRank(b.env));
  for (const env of ordered) {
    for (const route of env.routes) {
      for (const op of route.operations) {
        const key = `${op.method} ${route.path}`;
        if (!rows.has(key)) {
          rows.set(key, {
            key,
            method: op.method,
            path: route.path,
            exposure: route.exposure,
            anonymous: op.authentication === ANONYMOUS_AUTHENTICATION,
          });
        }
      }
    }
  }
  return [...rows.values()];
}

/**
 * Gateway hosts of an environment (D-15, AD-7 [v4], SR-03 [v2]). The
 * annotation carries no host: `byx-catalog` derives it from the environment
 * (`gatewayApiHost` and the OpenAPI `servers` of `synthesizeOpenApi`), and
 * this is a mirror of that formula. A host is never read from the
 * annotation. The source-text contract test (CDN-3,
 * `gateway.contract.test.ts`) fails when the backend formula changes.
 */

/** The environment whose host has no environment infix. */
export const GATEWAY_PROD_ENV = 'prod';

/** Prefix of the private host, which serves every route. */
export const GATEWAY_PRIVATE_HOST_PREFIX = 'pvt.';

/** The exposure of a route that the public host also serves. */
export const GATEWAY_PUBLIC_EXPOSURE: GatewayExposureValue = 'public';

/** The canonical gateway host of an environment (mirror of the backend). */
export function gatewayApiHost(env: string): string {
  return env === GATEWAY_PROD_ENV
    ? 'api.byxcapital.com.br'
    : `api.${env}.byxcapital.com.br`;
}

export type GatewayHosts = { internal: string; public?: string };

/**
 * The hosts of an environment: the private host always, the public host
 * only when the environment has at least one public route.
 */
export function gatewayHosts(
  env: string,
  hasPublicRoute: boolean,
): GatewayHosts {
  const host = gatewayApiHost(env);
  return {
    internal: `${GATEWAY_PRIVATE_HOST_PREFIX}${host}`,
    ...(hasPublicRoute ? { public: host } : {}),
  };
}

/** True when the route is also served by the public host. */
export function isPublicRoute(route: { exposure: GatewayExposureValue }) {
  return route.exposure === GATEWAY_PUBLIC_EXPOSURE;
}

/**
 * True when an environment name can be a DNS label (lower-case letters,
 * digits and inner hyphens). The environment comes from entity data, so a
 * name that is not one gets no derived host at all.
 */
export function isHostEnvLabel(env: string): boolean {
  return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(env);
}
